/**
 * Compact inputs of the tempo table and its forms (W2-08 timing): a text input that parses on commit (Enter or
 * blur), reverts on Escape, marks invalid text (aria-invalid plus the reason under the table) and shows a
 * placeholder for inherited values. `InlineBeat` parses every beat syntax of the editor (core/time/beatSyntax:
 * `12:3/4`, `12.75`, `+1/4`, `23.5s`, ...).
 */

import { useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { parseBeat } from '../../core/time/beatSyntax';
import type { Beat, BeatParseContext } from '../../core/time/types';
import s from './Timing.module.css';

export interface InlineTextProps<T> {
  /** Text shown while not editing ('' with a placeholder for inherited values). */
  readonly value: string;
  readonly placeholder?: string | undefined;
  readonly ariaLabel: string;
  parse(text: string): T | null;
  onCommit(v: T): void;
  /** Reports an invalid entry (or null when valid again). */
  readonly onInvalid?: ((invalid: boolean) => void) | undefined;
  readonly disabled?: boolean | undefined;
  readonly width?: string | undefined;
  readonly testId?: string | undefined;
  readonly autoFocus?: boolean | undefined;
}

export function InlineText<T>(props: InlineTextProps<T>) {
  const [draft, setDraft] = useState<string | null>(null);
  const invalid = draft !== null && draft.trim() !== '' && props.parse(draft) === null;
  const { onInvalid } = props;

  const commit = (): void => {
    if (draft === null) return;
    if (draft.trim() === '' || draft === props.value) {
      setDraft(null);
      onInvalid?.(false);
      return;
    }
    const v = props.parse(draft);
    if (v === null) {
      onInvalid?.(true);
      return;
    }
    setDraft(null);
    onInvalid?.(false);
    props.onCommit(v);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape' && draft !== null) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
      onInvalid?.(false);
    }
  };

  return (
    <input
      className={`${s.input} ${s.cellInput}`}
      style={props.width === undefined ? undefined : { width: props.width }}
      value={draft ?? props.value}
      placeholder={props.placeholder}
      aria-label={props.ariaLabel}
      aria-invalid={invalid}
      disabled={props.disabled}
      autoComplete="off"
      spellCheck={false}
      autoFocus={props.autoFocus}
      data-testid={props.testId}
      onChange={(e) => {
        setDraft(e.target.value);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => {
        commit();
        setDraft(null);
      }}
    />
  );
}

export interface InlineBeatProps extends Omit<InlineTextProps<Beat>, 'parse'> {
  readonly ctx: BeatParseContext;
}

export function InlineBeat(props: InlineBeatProps) {
  const { ctx, ...rest } = props;
  return (
    <InlineText<Beat>
      {...rest}
      parse={(text) => {
        const r = parseBeat(text, ctx);
        return r.ok ? r.beat : null;
      }}
    />
  );
}
